import { ReactNode, SyntheticEvent } from 'react';
import Countdown from '../Countdown';
import ExponentialCurve from '../../elements/ExponentialCurve';
import CircleTimer from '../CircleTimer';
import Price from '../../elements/Price';
import Button from '../../elements/Button';
import { useFormatter } from 'use-intl';
import NftQuantity from '../NftQuantity';

export interface AssetCardProps {
  onClick: () => void;
  tokenId: `${string}`;
  media: ReactNode;
  price?: bigint;
  isAuction?: boolean;
  expires: number;
  start: number;
  disabled?: boolean;
  selected?: boolean;
  shadow?: boolean;
  onDetailsClick: () => void;
  rarity?: number;
  rarityRank?: number;
  rarityPercentage: number;
  is1155: boolean;
  quantity: bigint | undefined;
  selectedQuantity: number;
  onQuantityChange: (quantity: number) => void;
}

const AssetCard = ({
  onClick,
  media,
  isAuction,
  price,
  tokenId,
  start,
  expires,
  disabled,
  selected,
  shadow = true,
  onDetailsClick,
  rarity,
  rarityPercentage,
  rarityRank,
  is1155,
  quantity,
  selectedQuantity,
  onQuantityChange,
}: AssetCardProps) => {
  const { number } = useFormatter();
  const hasRarity =
    rarity != null && rarityRank != null && rarityPercentage != null;

  return (
    <figure
      onClick={disabled ? undefined : onClick}
      className={`relative rounded-xl p-2 flex flex-col ${
        shadow
          ? 'shadow-[0_4px_28px_rgb(72_72_72_/_0.15)] border-gray-50 '
          : 'border-gray-200'
      } dark:border-gray-300 overflow-hidden border bg-gray-50 ${
        selected ? 'bg-pink-500/20 border-pink-500' : ''
      } ${disabled ? 'opacity-30 cursor-not-allowed' : 'cursor-pointer'} ${
        !selected && !disabled
          ? 'hover:border-gray-300 dark:hover:border-gray-500'
          : ''
      }`}
    >
      {media}
      {isAuction && (
        <span className="absolute top-0 right-0">
          <CircleTimer start={start} end={expires} />
        </span>
      )}
      {is1155 && (
        <NftQuantity
          maxQuantity={Number(quantity ?? 1n)}
          value={selectedQuantity}
          selected={!!selected}
          onChange={onQuantityChange}
        />
      )}
      <div className={`grid grid-cols-2 gap-2 items-end pt-2 mt-auto`}>
        <section className="flex flex-col font-medium text-xs space-y-1 mb-1">
          <h4 className="truncate">{`#${tokenId}`}</h4>
          {isAuction && price != null && (
            <div className="flex items-center">
              <span className="leading-none">
                <Price value={price} maximumSignificantDigits={4} />
              </span>
            </div>
          )}
          {!isAuction && price != null && (
            <span className="leading-none">
              <Price value={price} maximumSignificantDigits={4} />
            </span>
          )}
          {isAuction && (
            <footer className="text-xs font-bold">
              <Countdown endTime={expires} startTime={start} />
            </footer>
          )}
        </section>
        {isAuction && (
          <div className="h-14 mb-1">
            <ExponentialCurve start={start} finish={expires} />
          </div>
        )}
        <div className="col-span-2 flex space-x-2">
          <Button
            onClick={(e: SyntheticEvent) => {
              // dont trigger add to basket
              e.stopPropagation();
              onDetailsClick();
            }}
            size="xs"
            variant="tertiary"
            className={`w-full flex-1`}
          >
            {'Details'}
          </Button>
          {hasRarity && (
            <span
              className={`py-0.5 px-1 flex rounded-lg items-center text-xs font-medium ${
                rarityPercentage < 0.25
                  ? 'bg-gradient-pink-orange text-white'
                  : 'bg-gray-200'
              }`}
            >
              <span
                className={`${
                  rarityPercentage < 0.25 ? 'text-white' : 'text-gray-500'
                } w-4 h-4 mr-1`}
              >
                <svg viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                  <path
                    fill="currentColor"
                    d="M18.9 7.2l-3-4c-0.077-0.103-0.191-0.172-0.318-0.193l-6-1c-0.054-0.009-0.11-0.009-0.164 0l-6 1c-0.127 0.021-0.241 0.090-0.318 0.193l-3 4c-0.143 0.191-0.131 0.457 0.028 0.634l9 10c0.095 0.105 0.23 0.166 0.372 0.166s0.277-0.060 0.372-0.166l9-10c0.16-0.178 0.172-0.443 0.028-0.634zM12.786 7l-3.286 9.037-3.286-9.037h6.572zM6.707 6l2.793-2.793 2.793 2.793h-5.586zM10.957 3.25l3.698 0.616-1.233 1.849-2.466-2.465zM5.578 5.715l-1.233-1.849 3.698-0.616-2.465 2.465zM4.67 6.156l-2.985 0.597 1.791-2.388 1.194 1.791zM5.177 7.075l2.974 8.179-6.692-7.436 3.718-0.744zM13.823 7.075l3.718 0.744-6.692 7.436 2.974-8.179zM14.33 6.156l1.194-1.791 1.791 2.388-2.985-0.597z"
                  ></path>
                </svg>
              </span>
              <span className="leading-none">{number(rarityRank)}</span>
            </span>
          )}
        </div>
      </div>
    </figure>
  );
};

export default AssetCard;
